/** @jsx jsx */
import { jsx, useThemeUI } from "theme-ui"
import PropTypes from "prop-types"

import { findLink, useLinkTableNodes } from "../hooks/use-link-table"
import { buildBreadcrumbSlugs } from "../utils/breadcrumb-slugs"
import Link, { TransformLinkUri } from "./link"

const Breadcrumb = ({ language, languages, mode, location }) => {
  const { rawColors: colors } = useThemeUI().theme
  // One static query for the whole trail — looking the table up per level used
  // to call a hook inside .map() and behind an if.
  const links = useLinkTableNodes()

  const defaultLang = process.env.GATSBY_DEFAULT_LANG
    ? process.env.GATSBY_DEFAULT_LANG
    : "de"

  const crumbs = buildBreadcrumbSlugs(location.pathname, {
    languages,
    language,
    defaultLang,
  })

  // Resolve every level BEFORE rendering: a served path that is not a
  // Storyblok slug (e.g. an SEO URL) resolves to nothing and used to leave an
  // empty, padded `.breadcrumb` container behind (SUP-448).
  const levels = crumbs
    .map((crumb, index) => {
      const link = findLink(links, crumb.lookup, language)

      if (!link) {
        return null
      }

      if (link.is_folder) {
        const folderStartpage = findLink(links, crumb.folderLookup, language)

        if (!folderStartpage || !folderStartpage.is_startpage) {
          return null
        }
      }

      let alternate = { name: null, lang: null }

      if (process.env.GATSBY_SINGLE_LANGUAGE !== "true") {
        // link.alternates may not contain an entry for the current language
        // (Storyblok stories can have partial translation coverage). Fall
        // back to the empty placeholder so the SSR render of an ancestor
        // breadcrumb item doesn't crash.
        alternate =
          link.alternates?.find((alt) => alt.lang === language) || alternate
      }

      const name =
        language === defaultLang || !alternate.name ? link.name : alternate.name

      return { index, link, name }
    })
    .filter(Boolean)

  if (levels.length === 0) {
    return null
  }

  // The current-page marker goes on the deepest level that RESOLVED — the
  // last path segment of a listing page (/blog/page/2/) never does.
  const lastIndex = levels.length - 1

  return (
    <div
      className="breadcrumb"
      sx={{
        variant: "spacingContainer",
        fontSize: 0,
        my: 0,
        "& > span:not(:last-child)::after": {
          content: '">"',
          px: 2,
        },
        "& a": {
          color: colors.text,
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      }}
    >
      {levels.map(({ index, link, name }, position) => (
        <span className={`level-${index}`} key={link.uuid}>
          {position === lastIndex ? (
            <span
              sx={{
                color: colors.modes[mode].primary,
              }}
            >
              {name}
            </span>
          ) : (
            <Link to={TransformLinkUri(link.uuid, null, language)}>{name}</Link>
          )}
        </span>
      ))}
    </div>
  )
}

Breadcrumb.propTypes = {
  language: PropTypes.string.isRequired,
  languages: PropTypes.array.isRequired,
  mode: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
}

export default Breadcrumb
